/**
 * The agent pop-up (DEC-197, UI_SDD §R.13): why an agent failed, finished with problems
 * or was skipped, one click away from its wave row or its Flow node. The wave rows show
 * only a short status; the reason lives here, in plain words, with the technical detail
 * collapsed underneath.
 */
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { scansApi } from '../../api/scans'
import { ChevronRight, Copy } from 'lucide-react'
import type { AgentState } from '../../stores/scanStore'
import { AGENT_CATEGORY_CODE, type Severity } from '../../types/api'
import { compactNumber, durationLabel } from '../../utils/format'
import { Modal } from '../shared/Modal'
import { SeverityBadge } from '../shared/Badge'
import { agentDescription, agentStatusWord, tokenColor } from './waveModel'
import { agentHeadline, agentMeaning, agentRemedy, groupProblems, problemsOf, traceLines } from './failureModel'

interface AgentPopupProps {
  agent: AgentState
  scanId: string
  /** The scan is over, so a saved record (with technical detail) exists. */
  finished: boolean
  fromSaved: boolean
  onClose: () => void
  /** Starts a new scan of the same source; absent where that cannot work (uploads). */
  onRerun?: () => void
  rerunPending?: boolean
}

const SEVERITIES: Severity[] = ['critical', 'high', 'medium', 'low']

function dotFor(a: AgentState): string {
  if (a.status === 'failed') return tokenColor('mark-critical')
  if (a.status === 'skipped') return tokenColor('mark-idle')
  return tokenColor('mark-warn')
}

export function AgentPopup({ agent: a, scanId, finished, fromSaved, onClose, onRerun, rerunPending }: AgentPopupProps) {
  const problems = problemsOf(a)
  const groups = groupProblems(problems)
  const remedy = agentRemedy(a)
  // A live event carries no technical detail. Once the scan is over the saved record
  // does, so it is read from there (the Monitor's own cache key, fetched at most once).
  const needsSaved = finished && a.status === 'failed' && !a.failure?.detail
  const { data: saved } = useQuery({
    queryKey: ['scan-agents', scanId],
    queryFn: () => scansApi.agents(scanId),
    enabled: needsSaved,
    staleTime: Infinity,
    retry: false,
  })
  const savedFailure = saved?.agents.find((r) => r.agent_id === a.agent_id)?.failure ?? null
  const detail = a.failure?.detail ?? savedFailure?.detail ?? null
  const category = AGENT_CATEGORY_CODE[a.agent_id]
  const findingsPath = category ? `/scans/${scanId}/findings?category=${category}` : `/scans/${scanId}/findings`
  const kept = a.failure?.kept
  const findings = typeof kept?.findings === 'number' ? kept.findings : a.findings_count
  const tokens = typeof kept?.tokens === 'number' ? kept.tokens : a.tokens_used
  const warnCount = typeof kept?.warnings === 'number' ? kept.warnings : (a.warnings?.length ?? 0)
  const timeKnown = !fromSaved || a.timeRecorded
  const ran = a.status !== 'skipped'

  return (
    <Modal
      title={agentHeadline(a)}
      subtitle={`${a.label} · Wave ${a.wave} · ${agentStatusWord(a)}`}
      dot={dotFor(a)}
      onClose={onClose}
      testId="agent-popup"
    >
      <div className="flex flex-col gap-4 text-[13px] leading-[1.55] text-body">
        <p className="m-0" data-testid="agent-popup-meaning">
          {agentMeaning(a, agentDescription(a.agent_id))}
        </p>

        {a.failure?.step && a.status === 'failed' && (
          <p className="m-0 text-muted">It was {a.failure.step} when it stopped.</p>
        )}

        {ran && (
          <section aria-label="What was kept" className="rounded-md border border-hairline bg-fill-subtle px-4 py-3 flex flex-col gap-2">
            <span className="font-semibold text-[10px] tracking-[0.16em] uppercase text-muted">Kept</span>
            <div className="flex flex-wrap gap-x-6 gap-y-2">
              <Fact label="Findings" value={String(findings)} />
              <Fact label="Warnings" value={String(warnCount)} />
              <Fact label="Run time" value={timeKnown && a.duration_seconds > 0 ? durationLabel(a.duration_seconds) : 'not recorded'} />
              <Fact label="Tokens" value={timeKnown && tokens > 0 ? compactNumber(tokens) : timeKnown ? 'none' : 'not recorded'} />
            </div>
            {findings > 0 && (
              <div className="flex flex-wrap items-center gap-2">
                {SEVERITIES.filter((k) => (a.severity?.[k] ?? 0) > 0).map((k) => (
                  <SeverityBadge key={k} severity={k}>
                    {a.severity[k]} {k.charAt(0).toUpperCase() + k.slice(1)}
                  </SeverityBadge>
                ))}
                <Link to={findingsPath} onClick={onClose} className="font-semibold text-[12px] text-accent-text hover:underline">
                  View findings
                </Link>
              </div>
            )}
          </section>
        )}

        <section aria-label="Problems" className="flex flex-col gap-2">
          <span className="font-semibold text-[10px] tracking-[0.16em] uppercase text-muted">
            {a.status === 'skipped' ? 'Why' : 'What went wrong'}
          </span>
          {groups.length === 0 ? (
            <p className="m-0 text-muted">No message was saved for this agent.</p>
          ) : (
            <ul className="m-0 p-0 list-none flex flex-col gap-2" data-testid="agent-problems">
              {groups.map((g) => (
                <li key={g.text} className="rounded-md border border-hairline px-3 py-2">
                  {g.items.length === 1 ? (
                    <span>{g.text}</span>
                  ) : (
                    <details>
                      <summary className="cursor-pointer">
                        <span className="font-semibold text-ink">{g.items.length} times:</span> {g.text}
                      </summary>
                      <ul className="mt-2 mb-0 pl-4 text-[12px] text-muted">
                        {g.items.map((m, i) => (
                          <li key={`${i}-${m}`}>{m}</li>
                        ))}
                      </ul>
                    </details>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>

        {remedy && (
          <section aria-label="What to do" className="flex flex-col gap-2">
            <span className="font-semibold text-[10px] tracking-[0.16em] uppercase text-muted">What to do</span>
            <p className="m-0">{remedy.text}</p>
            {remedy.action === 'rerun' && onRerun && (
              <button
                type="button"
                onClick={onRerun}
                disabled={rerunPending}
                className="self-start font-semibold text-[12px] text-ink bg-ground border border-hairline rounded-md px-4 py-[8px] hover:bg-hover-hue disabled:opacity-50"
              >
                {rerunPending ? 'Starting…' : 'Run again'}
              </button>
            )}
            {remedy.action && typeof remedy.action === 'object' && (
              <Link
                to={remedy.action.to}
                className="self-start font-semibold text-[12px] text-ink bg-ground border border-hairline rounded-md px-4 py-[8px] hover:bg-hover-hue"
              >
                {remedy.action.label}
              </Link>
            )}
          </section>
        )}

        {a.status === 'failed' && <TechnicalDetails detail={detail} finished={finished} recorded={!!(a.failure ?? savedFailure)} />}
      </div>
    </Modal>
  )
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <span className="flex flex-col gap-[2px]">
      <span className="text-[11px] text-muted">{label}</span>
      <span className="font-medium tabular-nums text-[14px] text-ink">{value}</span>
    </span>
  )
}

function TechnicalDetails({
  detail,
  finished,
  recorded,
}: {
  detail: NonNullable<AgentState['failure']>['detail'] | null
  finished: boolean
  recorded: boolean
}) {
  const [copied, setCopied] = useState(false)
  const lines = traceLines(detail?.trace_tail)
  const text = [
    detail?.exception_type && detail?.exception_message
      ? `${detail.exception_type}: ${detail.exception_message}`
      : detail?.exception_type ?? detail?.exception_message ?? '',
    ...lines,
  ]
    .filter(Boolean)
    .join('\n')

  const copy = () => {
    void navigator.clipboard?.writeText(text).then(
      () => setCopied(true),
      () => setCopied(false),
    )
  }

  return (
    <details className="rounded-md border border-hairline" data-testid="technical-details">
      <summary className="cursor-pointer px-3 py-2 font-semibold text-[12px] text-accent-text inline-flex items-center gap-1">
        <ChevronRight className="w-3 h-3" /> Show technical details
      </summary>
      <div className="px-3 pb-3 flex flex-col gap-2">
        {text ? (
          <>
            <pre className="m-0 max-h-[220px] overflow-auto rounded-md bg-ground border border-hairline p-3 text-[11.5px] leading-[1.5] text-body whitespace-pre-wrap break-words">
              {text}
            </pre>
            <button
              type="button"
              onClick={copy}
              className="self-start inline-flex items-center gap-1 font-semibold text-[12px] text-body border border-hairline rounded-md px-3 py-[6px] hover:bg-hover-hue"
            >
              <Copy className="w-3 h-3" /> {copied ? 'Copied' : 'Copy'}
            </button>
          </>
        ) : (
          <p className="m-0 text-[12px] text-muted">
            {!recorded
              ? 'Technical details were not recorded for this scan.'
              : finished
                ? 'No technical detail was saved for this failure.'
                : 'The technical details are kept with the scan and show here once it finishes.'}
          </p>
        )}
      </div>
    </details>
  )
}
